// src/modules/cadete/PesquisaCadetes.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, paths } from '../../firebase/config';
import { 
    collection, 
    query, 
    onSnapshot,
    orderBy
} from "firebase/firestore";
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { formatarData, getNormalizedDateString } from '../../utils/helpers';

// --- Ícones ---
const SearchIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>;
const FilterIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>;

export const PesquisaCadetes = () => {
  const [loading, setLoading] = useState(true);
  
  // Dados
  const [escolhas, setEscolhas] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [locais, setLocais] = useState([]);
  const [cadetesMap, setCadetesMap] = useState({}); 
  const [relatoriosMap, setRelatoriosMap] = useState({}); 

  // Filtros
  const [filtroTurma, setFiltroTurma] = useState('');
  const [filtroLocal, setFiltroLocal] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Carregamento de Dados
  useEffect(() => {
    setLoading(true);
    
    // 1. Carrega Turmas e Locais
    const unsubTurmas = onSnapshot(collection(db, paths.turmas), (s) => setTurmas(s.docs.map(d => ({id: d.id, ...d.data()})).sort((a,b)=>a.nome.localeCompare(b.nome))));
    const unsubLocais = onSnapshot(collection(db, paths.locais), (s) => setLocais(s.docs.map(d => ({id: d.id, ...d.data()})).sort((a,b)=>a.nome.localeCompare(b.nome))));

    // 2. Carrega Usuários
    const unsubUsers = onSnapshot(collection(db, paths.users), (s) => {
        const map = {};
        s.docs.forEach(d => { map[d.id] = d.data(); });
        setCadetesMap(map);
    });

    // 3. Carrega Escolhas (Histórico)
    const qEscolhas = query(collection(db, paths.escolhas), orderBy("data", "desc"));
    const unsubEscolhas = onSnapshot(qEscolhas, (s) => {
        const list = s.docs.map(d => {
            const data = d.data();
            return {
                id: d.id,
                ...data,
                dataReal: data.data ? data.data.toDate() : null
            };
        });
        setEscolhas(list);
    });

    // 4. Carrega Relatórios
    const qRelatorios = query(collection(db, paths.relatoriosEstagio));
    const unsubRelatorios = onSnapshot(qRelatorios, (s) => {
        const map = {};
        s.docs.forEach(d => {
            const data = d.data();
            if (data.escolha_id) {
                map[data.escolha_id] = data;
            }
        });
        setRelatoriosMap(map);
        setLoading(false); 
    });

    return () => { unsubTurmas(); unsubLocais(); unsubUsers(); unsubEscolhas(); unsubRelatorios(); };
  }, []);

  // Lógica de Filtragem
  const filteredEscolhas = useMemo(() => {
    return escolhas.filter(escolha => {
        if (escolha.opcao_impossibilidade) return false; 

        // 1. Filtro de Texto
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            const relatorio = relatoriosMap[escolha.id];
            const preceptorNome = relatorio?.cadete?.qra_preceptor || '';
            
            const matchNome = escolha.cadete_nome && escolha.cadete_nome.toLowerCase().includes(term);
            const matchLocal = escolha.local_nome && escolha.local_nome.toLowerCase().includes(term);
            const matchPreceptor = preceptorNome.toLowerCase().includes(term);

            if (!matchNome && !matchLocal && !matchPreceptor) return false;
        }

        // 2. Filtros Select
        if (filtroTurma) {
            const cadeteData = cadetesMap[escolha.cadete_id];
            if (!cadeteData || cadeteData.turma_id !== filtroTurma) return false;
        }

        if (filtroLocal && escolha.local_id !== filtroLocal) return false;

        // 3. Filtro Data
        if (filtroDataInicio || filtroDataFim) {
             const dataEstagio = getNormalizedDateString(escolha.data);
             if (filtroDataInicio && dataEstagio < filtroDataInicio) return false;
             if (filtroDataFim && dataEstagio > filtroDataFim) return false;
        }

        return true;
    });
  }, [escolhas, searchTerm, filtroTurma, filtroLocal, filtroDataInicio, filtroDataFim, cadetesMap, relatoriosMap]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 animate-fade-in bg-gray-50 min-h-screen p-6 rounded-lg">
        {/* Cabeçalho */}
        <header className="flex flex-col md:flex-row justify-between items-center border-b border-gray-200 pb-4 gap-4">
            <div>
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <SearchIcon /> Pesquisa de Estágios
                </h1>
                <p className="text-sm text-gray-500 mt-1">Consulte a escala de serviço e histórico de estágios.</p>
            </div>
        </header>

        {/* Filtros */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">Turma</label>
                <select 
                    className="w-full border p-2 rounded text-sm bg-gray-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-blue-500"
                    value={filtroTurma}
                    onChange={e => setFiltroTurma(e.target.value)}
                >
                    <option value="">Todas as Turmas</option>
                    {turmas.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </select>
            </div>

            <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">Local de Estágio</label>
                <select 
                    className="w-full border p-2 rounded text-sm bg-gray-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-blue-500"
                    value={filtroLocal}
                    onChange={e => setFiltroLocal(e.target.value)}
                >
                    <option value="">Todos os Locais</option>
                    {locais.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
                </select>
            </div>

            <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">De (Data)</label>
                <input 
                    type="date" 
                    className="w-full border p-2 rounded text-sm bg-gray-50 focus:bg-white"
                    value={filtroDataInicio}
                    onChange={e => setFiltroDataInicio(e.target.value)}
                />
            </div>

             <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">Até (Data)</label>
                <input 
                    type="date" 
                    className="w-full border p-2 rounded text-sm bg-gray-50 focus:bg-white"
                    value={filtroDataFim}
                    onChange={e => setFiltroDataFim(e.target.value)}
                />
            </div>

            <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">Buscar (Nome, QRA, Preceptor)</label>
                <div className="relative">
                    <input 
                        className="w-full border p-2 pl-8 rounded text-sm bg-gray-50 focus:bg-white"
                        placeholder="Pesquisar..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                    <svg className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                </div>
            </div>
        </div>

        {/* Tabela de Resultados */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                <h3 className="font-bold text-gray-700 text-sm flex items-center gap-2">
                    <FilterIcon /> Resultados Encontrados
                </h3>
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-bold">
                    {filteredEscolhas.length} registros
                </span>
            </div>
            
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                    <thead className="bg-gray-100 text-gray-600 font-bold uppercase text-xs">
                        <tr>
                            <th className="px-4 py-3 w-32 whitespace-nowrap">Data</th>
                            <th className="px-4 py-3">Cadete / Aluno</th>
                            {/* ADICIONADO WHITESPACE-NOWRAP NA TURMA */}
                            <th className="px-4 py-3 whitespace-nowrap">Turma</th>
                            <th className="px-4 py-3">Local de Estágio</th>
                            <th className="px-4 py-3 text-indigo-700 whitespace-nowrap">Preceptor (QRA)</th>
                            <th className="px-4 py-3 text-indigo-700 whitespace-nowrap">Tel. Preceptor</th>
                            {/* ADICIONADO WHITESPACE-NOWRAP NO TURNO */}
                            <th className="px-4 py-3 text-center w-24 whitespace-nowrap">Turno</th>
                            <th className="px-4 py-3 text-center w-24">Tipo</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredEscolhas.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="px-4 py-8 text-center text-gray-500 italic">
                                    Nenhum estágio encontrado com os filtros selecionados.
                                </td>
                            </tr>
                        ) : (
                            filteredEscolhas.map((item) => {
                                const cadeteExtra = cadetesMap[item.cadete_id] || {};
                                const turmaNome = cadeteExtra.turma_nome || item.turma_nome || '-';
                                
                                const relatorio = relatoriosMap[item.id];
                                const preceptorQRA = relatorio?.cadete?.qra_preceptor || '-';
                                const preceptorTel = relatorio?.cadete?.telefone_preceptor || '-';

                                return (
                                    <tr key={item.id} className="hover:bg-blue-50/50 transition-colors">
                                        <td className="px-4 py-3 font-mono text-gray-600 font-medium whitespace-nowrap">
                                            {formatarData(item.data)}
                                        </td>
                                        <td className="px-4 py-3 font-bold text-gray-800">
                                            {item.cadete_nome}
                                        </td>
                                        {/* CÉLULA TURMA COM WHITESPACE-NOWRAP */}
                                        <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                                            {turmaNome}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700">
                                            {item.local_nome}
                                        </td>
                                        
                                        <td className="px-4 py-3 font-medium text-indigo-700 whitespace-nowrap">
                                            {preceptorQRA !== '-' ? preceptorQRA : <span className="text-gray-300 italic text-xs">Pendente</span>}
                                        </td>
                                        <td className="px-4 py-3 font-medium text-indigo-700 whitespace-nowrap">
                                            {preceptorTel !== '-' ? preceptorTel : <span className="text-gray-300 italic text-xs">--</span>}
                                        </td>

                                        {/* CÉLULA TURNO COM WHITESPACE-NOWRAP */}
                                        <td className="px-4 py-3 text-center whitespace-nowrap">
                                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                                                item.turno === '1º' ? 'bg-yellow-100 text-yellow-800' : 
                                                item.turno === '2º' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100'
                                            }`}>
                                                {item.turno} Turno
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase border ${
                                                item.tipo === 'PCO' ? 'bg-sky-50 text-sky-700 border-sky-100' : 
                                                item.tipo === 'PJ' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-gray-50 border-gray-100'
                                            }`}>
                                                {item.tipo}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
  );
};